/**
 * pixiGl — the Pixi-8.6.6 <-> raw-WebGL2 interop seam for the shaders type's
 * GPU systems field. THE Stage-1 risk, resolved here.
 *
 * Resolution (proven live on the 8.6.6 ANGLE-Metal renderer):
 *  - Pixi's renderer exposes its shared WebGL2 context at `renderer.gl`
 *    (preferWebGLVersion:2, PixiController). We run our OWN ping-pong GPGPU
 *    passes on that same context (our own FBO + textures), then draw the
 *    systems points into a Pixi `RenderTexture`'s GL framebuffer — reached via
 *    `renderer.renderTarget.getRenderTarget(rt)` -> `getGpuRenderTarget(rt)`
 *    -> `.framebuffer`. A plain Pixi Sprite(rt) then composites the field at
 *    the systems container's z-order (design §5 / §10 "use Pixi RenderTexture
 *    abstractions", not raw-GL to the default framebuffer).
 *  - Pixi re-binds its own render target + GL state at the start of every one
 *    of its renders, so as long as we save/restore the state WE touch
 *    (GLStateGuard) our passes cannot corrupt Pixi's state machine. Verified:
 *    after leaving a foreign framebuffer bound, Pixi recovered FRAMEBUFFER_
 *    BINDING to null on its next frame.
 *
 * __PIXI_APP__ is the app-global handle PixiController sets (also what the
 * pixel-oracle in docs/DECISIONS.md reads) — the contract (HomeWebGLDeps)
 * does not hand the renderer to the WebGL layer, so this global is the seam.
 * Everything is `any` against the v5 ambient PIXI types (see globals.d.ts).
 */

export interface PixiGL {
	renderer: any;
	gl: WebGL2RenderingContext;
}

/** The shared Pixi WebGL2 context, or null until the app/context exists (the
 *  GPU field lazy-inits on the first tick that returns non-null). */
export function getPixiGL(): PixiGL | null {
	const app = (globalThis as any).__PIXI_APP__;
	if (!app || !app.renderer || !app.renderer.gl) {
		return null;
	}
	const gl = app.renderer.gl;
	//WebGL1 (or a WebGPU renderer) cannot run the RGBA32F ping-pong — bail to
	//the capability fallback (design §10.1) rather than throw:
	if (typeof WebGL2RenderingContext !== 'undefined' && !(gl instanceof WebGL2RenderingContext)) {
		return null;
	}
	return { renderer: app.renderer, gl };
}

export interface RenderTextureTarget {
	framebuffer: WebGLFramebuffer;
	pixelWidth: number;
	pixelHeight: number;
}

/** The WebGLFramebuffer backing a Pixi RenderTexture, plus its device-pixel
 *  size (logical size x renderer resolution). Pixi caches both the coerced
 *  RenderTarget and its GL objects, so this is cheap to call per frame; we
 *  re-fetch on resize when the RenderTexture is recreated. */
export function getRenderTextureTarget(renderer: any, renderTexture: any): RenderTextureTarget | null {
	const renderTarget = renderer.renderTarget.getRenderTarget(renderTexture);
	const glRenderTarget = renderer.renderTarget.getGpuRenderTarget(renderTarget);
	if (!glRenderTarget || !glRenderTarget.framebuffer) {
		return null;
	}
	return {
		framebuffer: glRenderTarget.framebuffer,
		pixelWidth: renderTexture.source.pixelWidth,
		pixelHeight: renderTexture.source.pixelHeight
	};
}

/**
 * Saves and restores exactly the GL state our passes mutate, so a GPU systems
 * pass is hermetic regardless of when it runs relative to Pixi's own render.
 * (Pixi re-binds its state anyway, but a self-contained guard is the honest
 * "do not corrupt Pixi's GL state" contract, and keeps us safe against future
 * mid-frame call orderings.)
 */
export class GLStateGuard {
	private _gl: WebGL2RenderingContext;
	private _fbo: WebGLFramebuffer | null = null;
	private _viewport: Int32Array | null = null;
	private _program: WebGLProgram | null = null;
	private _arrayBuffer: WebGLBuffer | null = null;
	private _vao: WebGLVertexArrayObject | null = null;
	private _activeTexture = 0;
	private _tex0: WebGLTexture | null = null;
	private _tex1: WebGLTexture | null = null;
	private _blend = false;
	private _depthTest = false;
	private _scissor = false;
	private _blendSrcRgb = 0;
	private _blendDstRgb = 0;
	private _blendSrcAlpha = 0;
	private _blendDstAlpha = 0;

	constructor(gl: WebGL2RenderingContext) {
		this._gl = gl;
	}

	public save(): void {
		const gl = this._gl;
		this._fbo = gl.getParameter(gl.FRAMEBUFFER_BINDING);
		this._viewport = gl.getParameter(gl.VIEWPORT);
		this._program = gl.getParameter(gl.CURRENT_PROGRAM);
		this._arrayBuffer = gl.getParameter(gl.ARRAY_BUFFER_BINDING);
		this._vao = gl.getParameter(gl.VERTEX_ARRAY_BINDING);
		this._activeTexture = gl.getParameter(gl.ACTIVE_TEXTURE);
		gl.activeTexture(gl.TEXTURE0);
		this._tex0 = gl.getParameter(gl.TEXTURE_BINDING_2D);
		gl.activeTexture(gl.TEXTURE1);
		this._tex1 = gl.getParameter(gl.TEXTURE_BINDING_2D);
		this._blend = gl.getParameter(gl.BLEND);
		this._depthTest = gl.getParameter(gl.DEPTH_TEST);
		this._scissor = gl.getParameter(gl.SCISSOR_TEST);
		this._blendSrcRgb = gl.getParameter(gl.BLEND_SRC_RGB);
		this._blendDstRgb = gl.getParameter(gl.BLEND_DST_RGB);
		this._blendSrcAlpha = gl.getParameter(gl.BLEND_SRC_ALPHA);
		this._blendDstAlpha = gl.getParameter(gl.BLEND_DST_ALPHA);
	}

	public restore(): void {
		const gl = this._gl;
		gl.bindFramebuffer(gl.FRAMEBUFFER, this._fbo);
		if (this._viewport) {
			gl.viewport(this._viewport[0], this._viewport[1], this._viewport[2], this._viewport[3]);
		}
		gl.useProgram(this._program);
		gl.bindBuffer(gl.ARRAY_BUFFER, this._arrayBuffer);
		gl.bindVertexArray(this._vao);
		gl.activeTexture(gl.TEXTURE1);
		gl.bindTexture(gl.TEXTURE_2D, this._tex1);
		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, this._tex0);
		gl.activeTexture(this._activeTexture);
		this._toggle(gl.BLEND, this._blend);
		this._toggle(gl.DEPTH_TEST, this._depthTest);
		this._toggle(gl.SCISSOR_TEST, this._scissor);
		gl.blendFuncSeparate(this._blendSrcRgb, this._blendDstRgb, this._blendSrcAlpha, this._blendDstAlpha);
	}

	private _toggle(cap: number, on: boolean): void {
		if (on) {
			this._gl.enable(cap);
		} else {
			this._gl.disable(cap);
		}
	}
}
